import { useFetch } from '@/composables/useFetch';
import { clearImageCache } from '@/composables/useImageBase64';
import i18n from '@/i18n';
import { useAppStore } from '@/stores/appStore';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import type { User, UserLoginRequest, UserRegisterRequest } from '@/types/api';
import axios from '@/utils/axios';
import { AxiosError } from 'axios';
import { useToast } from 'primevue/usetoast';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { usePlatform } from './usePlatform';

// Helper function to decode JWT token (replaces decodeCredential from vue3-google-login)
function decodeCredential(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

const errors = ref<{ [key: string]: string[] }>({});
const isFetching = ref(false);

export const useAuth = () => {
  const { isNative, setAuthToken, removeAuthToken, getAuthToken, makeRequest, clearStorage } = usePlatform();
  const toast = useToast();
  const router = useRouter();
  const userStore = useUserStore();
  const envStore = useEnvStore();
  const t = (key) => i18n.global.t(key);

  async function login(userForm: UserLoginRequest, rememberMe: boolean, password: string) {
    userStore.clearUserData();
    await clearStorage();
    isFetching.value = true;

    userForm.password = password;
    userForm.remember = rememberMe;

    try {
      if (isNative.value) {
        console.log('isNative login');
        // Mobile login with token
        const response = await makeRequest({
          url: `${envStore.apiUrl}/mobile/login`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          data: {
            ...userForm,
            device_name: `Mobile App - ${new Date().toISOString()}`,
          },
        });

        console.log('Login response status:', response.status);
        console.log('Login response data:', response.data);

        if (response.status === 200) {
          const { user, token } = response.data as { user: User; token: string };

          // Store token
          await setAuthToken(token);

          // Initialize user store
          userStore.user = user;
          await userStore.initUser();

          toast.add({
            severity: 'success',
            summary: t('common.success'),
            detail: `${t('common.welcome')} ${user.first_name}!`,
            life: 3000,
          });

          useAppStore().setLanguage(user.language || envStore.defaultLocale);
          router.push({ name: userStore.isEmployee ? 'home' : 'home' });
        } else {
          // Handle non-200 responses
          console.error('Login failed with status:', response.status);
          console.error('Response data:', response.data);
          console.error('Full response:', JSON.stringify(response, null, 2));

          const errorData = response.data as Record<string, unknown>;
          if (errorData?.errors) {
            errors.value = errorData.errors as { [key: string]: string[] };
            console.error('Validation errors:', errors.value);
          }

          throw new Error((errorData?.message as string) || 'Login failed');
        }
      } else {
        console.log('isWeb login');
        // Web login with cookies
        if (!(await getCSRFCookie())) {
          console.log('no csrf');
          isFetching.value = false;
          return;
        }
        console.log('has csrf');

        await axios.post(`${envStore.apiUrl}/login`, userForm);
        console.log('logged in');
        await userStore.initUser();

        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: `${t('common.welcome')} ${userStore.user?.first_name}!`,
          life: 3000,
        });

        useAppStore().setLanguage(userStore.user?.language || envStore.defaultLocale);
        router.push({ name: userStore.isEmployee ? 'home' : 'home' });
      }
    } catch (error: unknown) {
      console.error('Login error caught:', error);

      if (error instanceof AxiosError && error.response?.data?.errors) {
        errors.value = error.response.data.errors;
      } else if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }

      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail:
          error instanceof AxiosError && error.response?.data?.message
            ? error.response.data.message
            : error instanceof Error
              ? error.message
              : t('auth.login.error'),
        life: 3000,
      });
    }

    isFetching.value = false;
  }

  async function register(userForm: UserRegisterRequest, password: string, passwordConfirmation: string, toast: ReturnType<typeof useToast>) {
    isFetching.value = true;

    // Patch primevue password component reactivity issue
    userForm.password = password;
    userForm.password_confirmation = passwordConfirmation;

    const language = useAppStore().language || useEnvStore().defaultLocale;
    userForm.language = language;

    try {
      if (isNative.value) {
        console.log('isNative register');
        // Mobile registration with token
        const response = await makeRequest({
          url: `${envStore.apiUrl}/mobile/register`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          data: {
            ...userForm,
            device_name: `Mobile App - ${new Date().toISOString()}`,
          },
        });

        console.log('Registration response status:', response.status);
        console.log('Registration response data:', response.data);

        if (response.status === 200 || response.status === 201) {
          const { user, token } = response.data as { user: User; token: string };

          // Store token FIRST
          await setAuthToken(token);
          console.log('Auth token stored after registration');

          // Set initial user data
          userStore.user = user;
          console.log('User data set in store:', user);

          // Wait a bit for backend to setup subscription
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Now fetch the complete user data with subscription details
          await userStore.initUser();
          console.log('User store initialized with complete data');

          toast.add({
            severity: 'success',
            summary: t('common.success'),
            detail: t('auth.registration.success'),
            life: 3000,
          });

          useAppStore().setLanguage(user.language || envStore.defaultLocale);

          // Redirect to validate-restaurant page to complete registration
          router.push({ name: 'validate-restaurant' });
        } else {
          // Handle non-success responses
          const errorData = response.data as Record<string, unknown>;
          if (errorData?.errors) {
            errors.value = errorData.errors as { [key: string]: string[] };
          }
          throw new Error((errorData?.message as string) || 'Registration failed');
        }
      } else {
        console.log('isWeb register');
        // Web registration with cookies
        // Sanctum CSRF
        if (!(await getCSRFCookie())) {
          isFetching.value = false;
          return;
        }

        // Register via custom web endpoint (automatically logs in the user)
        const response = await axios.post(useEnvStore().apiUrl + '/web/register', userForm);

        console.log('Registration successful:', response.status);

        // User is already logged in, initialize user store
        const userData = response.data.user as User;
        userStore.user = userData;

        console.log('User initialized:', userStore.user?.email);

        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('auth.registration.success'),
          life: 3000,
        });

        useAppStore().setLanguage(userStore.user?.language || envStore.defaultLocale);

        // Redirect to validate-restaurant page to complete registration
        router.push({ name: 'validate-restaurant' });
      }
    } catch (error: unknown) {
      console.error('Registration error caught:', error);

      if (error instanceof AxiosError && error.response?.data?.errors) {
        errors.value = error.response.data.errors;
      } else if (error instanceof Error) {
        console.error('Error message:', error.message);
      }

      const errorMessage =
        error instanceof AxiosError && error.response?.data?.message
          ? error.response.data.message
          : error instanceof Error
            ? error.message
            : t('auth.registration.error.message');

      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: errorMessage,
        life: 1500,
      });
    }

    isFetching.value = false;
  }

  async function logout() {
    isFetching.value = true;

    try {
      if (isNative.value) {
        // Mobile logout
        const token = await getAuthToken();
        if (token) {
          await makeRequest({
            url: `${envStore.apiUrl}/mobile/logout`,
            method: 'POST',
          });
        }
        await removeAuthToken();
      } else {
        // Web logout
        await axios.post(`${envStore.apiUrl}/logout`);
        // Clear token from localStorage if it exists (from magic link)
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }

    userStore.clearUserData();
    clearImageCache();
    await clearStorage();
    router.push({ name: 'login' });
    isFetching.value = false;
  }

  async function getCSRFCookie() {
    if (isNative.value) {
      console.log('isNative getCSRFCookie - skipping (not needed for mobile)');
      // Mobile doesn't need CSRF cookies
      return true;
    }

    console.log('isWeb getCSRFCookie');
    console.log('Current cookies:', document.cookie);

    try {
      console.log('envstore : ', envStore.apiUrl);
      await axios.get(`${envStore.apiUrl}/sanctum/csrf-cookie`);
      return true;
    } catch (error) {
      console.log('error fetching CSRF cookie: ', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('auth.registration.error.message'),
        life: 3000,
      });
      return false;
    }
  }

  async function sendResetLink(email: string) {
    isFetching.value = true;

    await axios
      .post(useEnvStore().apiUrl + '/forgot-password', { email: email })
      .then(() => {
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: `Un lien de récupération a été envoyé à ${email}`,
          life: 3000,
        });
      })
      .catch((error) => {
        if (error.response?.data?.errors) {
          errors.value = error.response.data.errors;
        }

        // Show toast for general error message
        const errorMessage = error.response?.data?.message || t('auth.forgotPassword.error');
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: errorMessage,
          life: 5000,
        });
      });

    isFetching.value = false;
  }

  async function resetPassword(email: string, password: string, password_confirmation: string, token: string) {
    isFetching.value = true;

    await axios
      .post(useEnvStore().apiUrl + '/reset-password', {
        email: email,
        password: password,
        password_confirmation: password_confirmation,
        token: token,
      })
      .then(() => {
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('auth.reset.success'),
          life: 3000,
        });
        router.push({ name: 'login' });
      })
      .catch((error) => {
        if (error.response?.data?.errors) {
          errors.value = error.response.data.errors;
        }

        // Show toast for general error message (e.g., invalid/expired token)
        const errorMessage = error.response?.data?.message || t('auth.reset.error');
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: errorMessage,
          life: 5000,
        });
      });

    isFetching.value = false;
  }

  async function loginWithGoogle(info) {
    console.log('loginWithGoogle', info);
    const userData = decodeCredential(info.credential);
    console.log('userData', userData);
    const language = useAppStore().language || useEnvStore().defaultLocale;

    // Préparer les données de la requête
    interface GoogleLoginRequest {
      id_token: string;
      language: 'fr' | 'en' | 'de' | 'it';
      country: string;
      device_name?: string;
      remember_me?: boolean;
    }

    const requestData: GoogleLoginRequest = {
      id_token: info.credential,
      language: language as 'fr' | 'en' | 'de' | 'it',
      country: 'CH',
    };

    // Ajouter device_name pour les plateformes natives
    if (isNative.value) {
      requestData.device_name = `Mobile App - Google - ${new Date().toISOString()}`;
      requestData.remember_me = true;
    }

    try {
      let response;

      if (isNative.value) {
        // Use makeRequest for native to ensure proper token handling
        response = await makeRequest({
          url: `${envStore.apiUrl}/auth/social/google`,
          method: 'POST',
          data: requestData,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        // Check for errors in the response (makeRequest doesn't throw)
        if (response.data?.error || response.status >= 400) {
          console.error('[loginWithGoogle] Backend error:', response.data);

          // Map backend error codes to translated messages
          let errorMessage = t('auth.login.error');
          if (response.data?.error === 'account_creation_failed') {
            errorMessage = t('auth.login.accountCreationFailed');
          } else if (response.data?.message) {
            errorMessage = response.data.message;
          }

          toast.add({
            severity: 'error',
            summary: t('common.error'),
            detail: errorMessage,
            life: 5000,
          });
          return;
        }

        // Store token FIRST before any other API calls
        if (response.data.token) {
          await setAuthToken(response.data.token);
          console.log('[loginWithGoogle] Token stored successfully');
        }
      } else {
        // Web: use axios as before
        const axiosResponse = await axios.post(envStore.apiUrl + '/auth/social/google', requestData);
        response = {
          data: axiosResponse.data,
          status: axiosResponse.status,
          headers: axiosResponse.headers,
        };
      }

      const newUser = response.data.newUser;

      // Now initUser can safely call /api/me with the stored token
      await userStore.initUser();

      // Check if user was properly initialized
      if (!userStore.user) {
        console.error('[loginWithGoogle] User initialization failed');
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('auth.login.error'),
          life: 5000,
        });
        return;
      }

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: `${t('common.welcome')} ${userStore.user.first_name} !`,
        life: 3000,
      });

      useAppStore().setLanguage(userStore.user?.language || useEnvStore().defaultLocale);

      if (newUser) router.push({ name: 'validate-restaurant' });
      else router.push({ name: userStore.isEmployee ? 'home' : 'home' });
    } catch (error: unknown) {
      console.error('Google login error:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } };
        if (axiosError.response?.data?.errors) {
          errors.value = axiosError.response.data.errors;
        }
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: axiosError.response?.data?.message || t('common.errorOccurred'),
          life: 5000,
        });
      }
    }
  }

  async function loginWithApple(info) {
    console.log('loginWithApple', info);
    const language = useAppStore().language || useEnvStore().defaultLocale;

    // Préparer les données de la requête
    interface AppleLoginRequest {
      id_token: string;
      language: 'fr' | 'en' | 'de' | 'it';
      country: string;
      device_name?: string;
      remember_me?: boolean;
      authorization_code?: string;
      first_name?: string;
      last_name?: string;
    }

    const requestData: AppleLoginRequest = {
      id_token: info.idToken,
      language: language as 'fr' | 'en' | 'de' | 'it',
      country: 'CH',
    };

    // Add first/last name if available from profile
    if (info.profile?.givenName) {
      requestData.first_name = info.profile.givenName;
    }
    if (info.profile?.familyName) {
      requestData.last_name = info.profile.familyName;
    }

    // Add authorization code if available
    if (info.authorizationCode) {
      requestData.authorization_code = info.authorizationCode;
    }

    // Ajouter device_name pour les plateformes natives
    if (isNative.value) {
      requestData.device_name = `Mobile App - Apple - ${new Date().toISOString()}`;
      requestData.remember_me = true;
    }

    try {
      let response;

      if (isNative.value) {
        // Use makeRequest for native to ensure proper token handling
        response = await makeRequest({
          url: `${envStore.apiUrl}/auth/social/apple`,
          method: 'POST',
          data: requestData,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        // Check for errors in the response (makeRequest doesn't throw)
        if (response.data?.error || response.status >= 400) {
          console.error('[loginWithApple] Backend error:', response.data);

          // Map backend error codes to translated messages
          let errorMessage = t('auth.login.error');
          if (response.data?.error === 'account_creation_failed') {
            errorMessage = t('auth.login.accountCreationFailed');
          } else if (response.data?.message) {
            errorMessage = response.data.message;
          }

          toast.add({
            severity: 'error',
            summary: t('common.error'),
            detail: errorMessage,
            life: 5000,
          });
          return;
        }

        // Store token FIRST before any other API calls
        if (response.data.token) {
          await setAuthToken(response.data.token);
          console.log('[loginWithApple] Token stored successfully');
        }
      } else {
        // Web: use axios as before
        const axiosResponse = await axios.post(envStore.apiUrl + '/auth/social/apple', requestData);
        response = {
          data: axiosResponse.data,
          status: axiosResponse.status,
          headers: axiosResponse.headers,
        };
      }

      const newUser = response.data.newUser;

      // Now initUser can safely call /api/me with the stored token
      await userStore.initUser();

      // Check if user was properly initialized
      if (!userStore.user) {
        console.error('[loginWithApple] User initialization failed');
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('auth.login.error'),
          life: 5000,
        });
        return;
      }

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: `${t('common.welcome')} ${userStore.user.first_name} !`,
        life: 3000,
      });

      useAppStore().setLanguage(userStore.user.language || useEnvStore().defaultLocale);

      if (newUser) router.push({ name: 'validate-restaurant' });
      else router.push({ name: userStore.isEmployee ? 'home' : 'home' });
    } catch (error: unknown) {
      console.error('Apple login error:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } };
        if (axiosError.response?.data?.errors) {
          errors.value = axiosError.response.data.errors;
        }
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: axiosError.response?.data?.message || t('common.errorOccurred'),
          life: 5000,
        });
      }
    }
  }

  async function validateStepTwoAuth(userForm, toast: ReturnType<typeof useToast>) {
    isFetching.value = true;
    const url = useEnvStore().apiUrl + '/users/step-two-registration';
    const { fetchData } = useFetch();
    const { data, error } = await fetchData(url, 'POST', userForm);
    userStore.user = data as User;
    if (error) {
      if (error.details && error.details.errors) {
        errors.value = error.details.errors;
      }
      const errorMessage = (error.details && error.details.message) || t('auth.registration.error.message');
      toast.add({ severity: 'error', summary: t('common.error'), detail: errorMessage, life: 1500 });
    } else {
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('auth.registration.success'),
        life: 3000,
      });
      router.push({ path: '/' });
    }
    isFetching.value = false;
  }

  function redirectHome() {
    console.log(router);
    router.push({ name: userStore.isEmployee ? 'home' : 'home' });
  }

  async function completeRegistration(
    userForm: UserRegisterRequest,
    password: string,
    passwordConfirmation: string,
    toast: ReturnType<typeof useToast>,
  ) {
    isFetching.value = true;

    // Patch primevue password component reactivity issue
    userForm.password = password;
    userForm.password_confirmation = passwordConfirmation;

    const language = useAppStore().language || useEnvStore().defaultLocale;
    userForm.language = language;

    // Sanctum CSRF
    if (!(await getCSRFCookie())) {
      isFetching.value = false;
      return;
    }

    // Register
    await axios
      .post(useEnvStore().apiUrl + '/complete-registration', userForm)
      .then(() => {
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('auth.registration.success'),
          life: 3000,
        });
        router.push({ path: '/' });
      })
      .catch((error) => {
        if (error.response.data.errors) errors.value = error.response.data.errors;
        const errorMessage = error.response.data.message ?? t('auth.registration.error.message');
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: errorMessage,
          life: 1500,
        });
      });

    isFetching.value = false;
  }

  async function deleteAccount(
    password: string,
    deleteDishes = false,
    restaurants: Array<{ restaurant_id: string; action: string; new_owner_id?: string }> = [],
  ) {
    isFetching.value = true;

    try {
      const response = await makeRequest({
        method: 'DELETE',
        url: `${envStore.apiUrl}/user/account`,
        data: {
          password,
          delete_dishes: deleteDishes,
          restaurants,
        },
      });

      if (response?.data) {
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('profile.profile.delete_account.account_deleted'),
          life: 3000,
        });

        // Clear user data and logout
        userStore.clearUserData();
        await clearStorage();
        router.push({ name: 'login' });

        isFetching.value = false;
        return true;
      }
    } catch (error: unknown) {
      console.error('Account deletion error:', error);

      if (error instanceof AxiosError && error.response) {
        const errorMessage = error.response.data?.message || t('profile.profile.delete_account.deletion_failed');

        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: errorMessage,
          life: 3000,
        });

        if (error.response.data?.errors) {
          errors.value = error.response.data.errors;
        }
      } else {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('profile.profile.delete_account.deletion_failed'),
          life: 3000,
        });
      }
    }

    isFetching.value = false;
    return false;
  }

  return {
    login,
    register,
    completeRegistration,
    logout,
    deleteAccount,
    errors,
    isFetching,
    user: userStore.user,
    sendResetLink,
    resetPassword,
    redirectHome,
    loginWithGoogle,
    loginWithApple,
    validateStepTwoAuth,
  };
};
